import {
  Directive,
  HostListener,
  Inject,
  ElementRef,
  Renderer2,
  Input
} from '@angular/core';
import { ValueAccessorProviderFactory } from '../value-accessor/value-accessor-provider-factory';
import { BaseNumberInputDirective } from './base-number-input.directive';

import type { OnChanges } from '@angular/core';
import type { ControlValueAccessor } from '@angular/forms';

/**
 * Rural Route or Po Box Input Directive
 *
 * Allows an input type number to have a max length of 4 for rural route and 5 for po box
 *
 * @example
 * <input type="number" [tdWbRuralRoutePoBoxInput]="RURAL_ROUTE" />
 */
@Directive({
  selector: 'input[tdWbRuralRoutePoBoxInput]',
  providers: [ValueAccessorProviderFactory(RuralRoutePoBoxInputDirective)]
})
export class RuralRoutePoBoxInputDirective extends BaseNumberInputDirective implements ControlValueAccessor, OnChanges {
  private readonly PO_BOX_MAX_LENGTH: number = 5;
  private readonly RURAL_ROUTE_MAX_LENGTH: number = 4;
  @Input('tdWbRuralRoutePoBoxInput')
  set inputType(value: string) { this._inputType = value;}

  get inputType(): string { return this._inputType; }

  private _inputType: string = 'RURAL_ROUTE';

  constructor(@Inject(ElementRef) protected elementRef: ElementRef,
              @Inject(Renderer2) protected renderer: Renderer2) {
    super('true', elementRef, renderer);
  }

  // Switch to keydown for better consistency and to handle IME/navigation keys correctly.
  // Allow only digits and enforce max length; allow deletion/navigation.
  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const el = this.elementRef.nativeElement as HTMLInputElement;
    const key = event.key;

    // Allow control/meta/alt shortcuts and navigation/deletion keys
    if (event.ctrlKey || event.metaKey || event.altKey) { return; }
    if (
      key === 'Backspace' || key === 'Delete' || key === 'Tab' ||
      key === 'Enter' || key === 'Escape' || key === 'Home' || key === 'End' ||
      key.startsWith('Arrow')
    ) {
      return; // never block these
    }

    // Only handle single-character (printable) keys from here
    if (key.length !== 1) { return; }

    // Digits only
    if (!/^[0-9]$/.test(key)) {
      event.preventDefault();
      return;
    }

    const value = el.value ?? '';
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const selected = Math.max(0, end - start);
    const max = this.maxLength();

    // Remaining capacity after replacing current selection
    const remaining = max - (value.length - selected);
    if (remaining <= 0) {
      event.preventDefault();
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    // Avoid base paste handler running and double-processing
    event.stopImmediatePropagation?.();
    event.stopPropagation();
    event.preventDefault();

    const input = event.target as HTMLInputElement;
    const before = input.value ?? '';
    const start = input.selectionStart ?? before.length;
    const end = input.selectionEnd ?? before.length;

    // Keep digits only, then truncate to capacity based on current selection
    const digits = (event.clipboardData?.getData('text') ?? '').replace(/[^0-9]+/g, '');
    const max = this.maxLength();
    const capacity = Math.max(0, max - (before.length - (end - start)));
    const insert = digits.slice(0, capacity);

    const next = before.slice(0, start) + insert + before.slice(end);
    this.renderer.setProperty(input, 'value', next);

    // Update internal state and model similar to the base input path
    const viewValue = next;
    let value: number;
    if (!this.isEmpty(viewValue)) {
      value = this.parseValue(viewValue);
    }
    this.updateModel(value);
    this.viewValue = viewValue;
  }

  ngOnChanges(): void {
    super.onChanges();
  }

  protected formatValue(value: number): string {
    return value?.toString();
  }

  protected parseValue(value: string): number {
    return Number(value);
  }

  private maxLength(): number {
    return this.inputType === 'PO_BOX' ? this.PO_BOX_MAX_LENGTH : this.RURAL_ROUTE_MAX_LENGTH;
  }
}
